import React, { useState, useEffect } from 'react';
import { Form, Button, Spinner, Alert } from '@/components/ui';
import { workIdService, type WorkIdDetail } from '../../../services/workIdService';
import ResponsiveModal from '../../../components/ResponsiveModal';

interface WorkIdUnmergeModalProps {
  visible: boolean;
  onClose: () => void;
  workId: WorkIdDetail | null;
  onSuccess: () => void;
}

const WorkIdUnmergeModal: React.FC<WorkIdUnmergeModalProps> = ({
  visible,
  onClose,
  workId,
  onSuccess,
}) => {
  const [detail, setDetail] = useState<WorkIdDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  const mergedIntoParent = detail?.mergedIntoParent;

  useEffect(() => {
    if (visible && workId?.id) {
      setDetail(workId);
      setReason('');
      setError(null);
      setValidationError(null);
      if (!workId.mergedIntoParent) {
        setLoading(true);
        workIdService
          .getById(workId.id)
          .then(setDetail)
          .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load work ID'))
          .finally(() => setLoading(false));
      }
    }
  }, [visible, workId?.id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!detail?.id || !mergedIntoParent) return;

    const trimmed = reason.trim();
    if (!trimmed) {
      setValidationError('Reason is required.');
      return;
    }
    setValidationError(null);
    setError(null);
    setSubmitting(true);

    try {
      await workIdService.unmerge(detail.id, {
        otherWorkIdId: mergedIntoParent.id,
        comments: trimmed,
      });
      onClose();
      onSuccess();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to unmerge work ID');
    } finally {
      setSubmitting(false);
    }
  };

  if (!workId) return null;

  const title = `Unmerge Work ID – ${workId.workIdentificationId ?? workId.id}`;

  return (
    <ResponsiveModal
      isOpen={visible}
      onClose={onClose}
      title={title}
      size="medium"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="warning"
            onClick={handleSubmit}
            disabled={
              submitting ||
              !reason.trim() ||
              loading ||
              !mergedIntoParent
            }
          >
            {submitting ? (
              <>
                <Spinner animation="border" size="sm" className="tw-mr-1" />
                Unmerging…
              </>
            ) : (
              'Unmerge'
            )}
          </Button>
        </>
      }
    >
      {loading ? (
        <div className="tw-text-center tw-py-4">
          <Spinner animation="border" />
        </div>
      ) : !mergedIntoParent ? (
        <p className="tw-text-content-muted tw-mb-0">
          Parent work ID could not be loaded. This work ID may not be in a merged state.
        </p>
      ) : (
        <Form onSubmit={handleSubmit}>
          {error && (
            <Alert variant="danger">
              {error}
            </Alert>
          )}

          <p className="tw-text-content-muted tw-mb-3">
            This work ID is merged into the parent below. Enter a reason to unmerge; the work ID will return to NEW status.
          </p>

          <Form.Group className="tw-mb-3">
            <Form.Label>Parent Work ID (merged into)</Form.Label>
            <Form.Control
              type="text"
              plaintext
              readOnly
              className="tw-bg-surface-alt tw-border tw-border-line tw-rounded tw-px-3 tw-py-2"
              value={`#${mergedIntoParent.id}${mergedIntoParent.workIdentificationId ? ` · ${mergedIntoParent.workIdentificationId}` : ''}`}
            />
          </Form.Group>

          <Form.Group className="tw-mb-0">
            <Form.Label>Reason (required)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                setValidationError(null);
              }}
              placeholder="e.g. Merged by mistake"
              isInvalid={!!validationError}
            />
            {validationError && (
              <Form.Control.Feedback type="invalid">
                {validationError}
              </Form.Control.Feedback>
            )}
          </Form.Group>
        </Form>
      )}
    </ResponsiveModal>
  );
};

export default WorkIdUnmergeModal;
